// CCAvenuePaymentStatus.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { postData } from '../../../../services/apiService';
import {
    CheckCircle,
    XCircle,
    AlertCircle,
    Calendar,
    Clock,
    Info,
    User,
    FileText,
    PartyPopper,
    GraduationCap,
    BookOpen,
    CreditCard,
    Receipt,
    Download,
    ArrowLeft,
    RefreshCw,
    Mail,
    Phone,
    Building,
    Hash,
    MessageCircle
} from 'lucide-react';
import Swal from 'sweetalert2';

const CCAvenuePaymentStatus = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const user = useSelector((state) => state.user.user);
    
    const [loading, setLoading] = useState(true);
    const [paymentData, setPaymentData] = useState(null);
    const [studentDetails, setStudentDetails] = useState(null);
    const [status, setStatus] = useState('loading');
    const [transactionDate, setTransactionDate] = useState('');
    const [error, setError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        const verifyPayment = async () => {
            try {
                setLoading(true);
                setError(null);

                const urlParams = new URLSearchParams(location.search);

                // ============================================================
                // STEP 1: Check for POST response data from session storage
                // ============================================================
                let postResponseData = null;
                try {
                    const stored = sessionStorage.getItem('ccavenueResponseData');
                    if (stored) {
                        postResponseData = JSON.parse(stored);
                    }
                } catch (error) {
                    console.error('Error reading POST response data:', error);
                }

                // ============================================================
                // STEP 2: If we have POST response data, use it
                // ============================================================
                if (postResponseData) {
                    const isCancelledFromPost = postResponseData.isCancelled;
                    const isSuccessFromPost = postResponseData.isSuccess;
                                        
                    if (isCancelledFromPost) {
                        setStatus('cancelled');
                        setPaymentData({
                            status: 'CANCELLED',
                            amount: parseFloat(postResponseData.amount) || 0,
                            txnId: postResponseData.txnId || 'N/A',
                            orderId: postResponseData.orderId || 'N/A',
                            receiptNo: postResponseData.receiptNo || `REC${Date.now()}`,
                            email: user?.email,
                            phone: user?.phone,
                            paymentMode: 'CCAvenue',
                            timestamp: postResponseData.timestamp || new Date().toISOString(),
                            txndate: postResponseData.timestamp || new Date().toISOString()
                        });
                        setStudentDetails({
                            name: user?.name || 'Student',
                            rollNo: user?.roll_no || 'N/A',
                            department: user?.department || user?.cour_LName || 'N/A',
                            semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                            academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                            email: user?.email,
                            phone: user?.phone
                        });
                        setTransactionDate(new Date().toLocaleString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }));
                        setLoading(false);
                        return;
                    } else if (isSuccessFromPost) {
                        setStatus('success');
                        setPaymentData({
                            status: 'SUCCESS',
                            amount: parseFloat(postResponseData.amount) || 0,
                            txnId: postResponseData.txnId || 'N/A',
                            orderId: postResponseData.orderId || 'N/A',
                            receiptNo: postResponseData.receiptNo || `REC${Date.now()}`,
                            email: user?.email,
                            phone: user?.phone,
                            paymentMode: 'CCAvenue',
                            timestamp: postResponseData.timestamp || new Date().toISOString(),
                            txndate: postResponseData.timestamp || new Date().toISOString()
                        });
                        setStudentDetails({
                            name: user?.name || 'Student',
                            rollNo: user?.roll_no || 'N/A',
                            department: user?.department || user?.cour_LName || 'N/A',
                            semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                            academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                            email: user?.email,
                            phone: user?.phone
                        });
                        setTransactionDate(new Date().toLocaleString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }));
                        setLoading(false);
                        return;
                    } else {
                        // Failed
                        setStatus('failed');
                        setPaymentData({
                            status: 'FAILED',
                            amount: parseFloat(postResponseData.amount) || 0,
                            txnId: postResponseData.txnId || 'N/A',
                            orderId: postResponseData.orderId || 'N/A',
                            receiptNo: postResponseData.receiptNo || `REC${Date.now()}`,
                            email: user?.email,
                            phone: user?.phone,
                            paymentMode: 'CCAvenue',
                            timestamp: postResponseData.timestamp || new Date().toISOString(),
                            txndate: postResponseData.timestamp || new Date().toISOString()
                        });
                        setStudentDetails({
                            name: user?.name || 'Student',
                            rollNo: user?.roll_no || 'N/A',
                            department: user?.department || user?.cour_LName || 'N/A',
                            semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                            academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                            email: user?.email,
                            phone: user?.phone
                        });
                        setTransactionDate(new Date().toLocaleString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        }));
                        setLoading(false);
                        return;
                    }
                }

                // ============================================================
                // STEP 3: Check URL params directly (for GET requests)
                // ============================================================
                const encResp = urlParams.get('encResp');
                const orderId = urlParams.get('orderId') || urlParams.get('ORDERID');
                const statusParam = urlParams.get('status') || urlParams.get('STATUS');
                const txnId = urlParams.get('txnId') || urlParams.get('TXNID');
                const amount = urlParams.get('amount') || urlParams.get('TXNAMOUNT');
                const cancelParam = urlParams.get('cancel');

                // Check if cancelled
                const isCancelled = 
                    cancelParam === 'true' ||
                    statusParam === 'cancelled' ||
                    statusParam === 'CANCELLED' ||
                    statusParam === 'cancel' ||
                    statusParam === 'Cancel';

                if (isCancelled) {
                    setStatus('cancelled');
                    setPaymentData({
                        status: 'CANCELLED',
                        amount: parseFloat(amount) || 0,
                        txnId: txnId || 'N/A',
                        orderId: orderId || 'N/A',
                        receiptNo: `REC${Date.now()}`,
                        email: user?.email,
                        phone: user?.phone,
                        paymentMode: 'CCAvenue',
                        timestamp: new Date().toISOString(),
                        txndate: new Date().toISOString()
                    });
                    setStudentDetails({
                        name: user?.name || 'Student',
                        rollNo: user?.roll_no || 'N/A',
                        department: user?.department || user?.cour_LName || 'N/A',
                        semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                        academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                        email: user?.email,
                        phone: user?.phone
                    });
                    setTransactionDate(new Date().toLocaleString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }));
                    setLoading(false);
                    return;
                }

                // Check if success
                const isSuccess = 
                    statusParam === 'success' ||
                    statusParam === 'Success' ||
                    statusParam === 'TXN_SUCCESS';

                if (isSuccess) {
                    setStatus('success');
                    setPaymentData({
                        status: 'SUCCESS',
                        amount: parseFloat(amount) || 0,
                        txnId: txnId || 'N/A',
                        orderId: orderId || 'N/A',
                        receiptNo: `REC${Date.now()}`,
                        email: user?.email,
                        phone: user?.phone,
                        paymentMode: 'CCAvenue',
                        timestamp: new Date().toISOString(),
                        txndate: new Date().toISOString()
                    });
                    setStudentDetails({
                        name: user?.name || 'Student',
                        rollNo: user?.roll_no || 'N/A',
                        department: user?.department || user?.cour_LName || 'N/A',
                        semester: user?.semNo ? `Semester ${user.semNo}` : 'N/A',
                        academicYear: user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A',
                        email: user?.email,
                        phone: user?.phone
                    });
                    setTransactionDate(new Date().toLocaleString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }));
                    setLoading(false);
                    return;
                }

                // ============================================================
                // STEP 4: If we have encrypted response, verify with backend
                // ============================================================
                if (encResp) {
                    try {
                        const response = await postData('/Fees/VerifyCCAvenuePayment', {
                            encResp: encResp,
                            orderId: orderId,
                            txnId: txnId
                        });

                        if (response?.success && response?.data) {
                            const paymentStatus = response.data.status || response.data.order_status;
                            const isSuccess = paymentStatus === 'Success' || 
                                            paymentStatus === 'TXN_SUCCESS' ||
                                            paymentStatus === 'success';

                            setStatus(isSuccess ? 'success' : 'failed');
                            
                            setPaymentData({
                                status: paymentStatus,
                                amount: parseFloat(response.data.amount) || parseFloat(amount) || 0,
                                txnId: response.data.txnId || txnId || `TXN${Date.now()}`,
                                orderId: response.data.orderId || orderId,
                                receiptNo: response.data.receiptNo || `REC${Date.now()}`,
                                email: response.data.email || user?.email,
                                phone: response.data.phone || user?.phone,
                                paymentMode: 'CCAvenue',
                                timestamp: new Date().toISOString(),
                                txndate: response.data.txndate || new Date().toISOString()
                            });

                            setStudentDetails({
                                name: response.data.studentName || user?.name || 'Student',
                                rollNo: response.data.rollNo || user?.roll_no || 'N/A',
                                department: response.data.department || user?.department || user?.cour_LName || 'N/A',
                                semester: response.data.semester || (user?.semNo ? `Semester ${user.semNo}` : 'N/A'),
                                academicYear: response.data.academicYear || (user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A'),
                                email: response.data.email || user?.email,
                                phone: response.data.phone || user?.phone
                            });

                            const date = new Date(response.data.timestamp || response.data.txndate || Date.now());
                            setTransactionDate(date.toLocaleString('en-US', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }));

                            setLoading(false);
                            return;
                        }
                    } catch (error) {
                        console.error('Verification error:', error);
                    }
                }

                // ============================================================
                // STEP 5: Try stored data as fallback
                // ============================================================
                let storedData = null;
                try {
                    const ccavenueStored = sessionStorage.getItem('ccavenuePaymentData');
                    if (ccavenueStored) {
                        const parsed = JSON.parse(ccavenueStored);
                        if (parsed && parsed.expires > Date.now()) {
                            storedData = parsed;
                        }
                    }
                } catch (error) {
                    console.error('Error parsing stored data:', error);
                }

                if (storedData) {
                    setStatus('processing');
                    setPaymentData({
                        status: 'PENDING',
                        amount: parseFloat(storedData.payment?.amount) || 0,
                        txnId: storedData.payment?.txnId || 'N/A',
                        orderId: storedData.payment?.orderId || 'N/A',
                        receiptNo: storedData.payment?.receiptNo || `REC${Date.now()}`,
                        email: storedData.payment?.email || user?.email,
                        phone: storedData.payment?.phone || user?.phone,
                        paymentMode: 'CCAvenue',
                        timestamp: storedData.payment?.timestamp || new Date().toISOString(),
                        txndate: storedData.payment?.txndate || new Date().toISOString()
                    });
                    setStudentDetails({
                        name: storedData.student?.name || user?.name || 'Student',
                        rollNo: storedData.student?.rollNo || user?.roll_no || 'N/A',
                        department: storedData.student?.department || user?.department || user?.cour_LName || 'N/A',
                        semester: storedData.student?.semester || (user?.semNo ? `Semester ${user.semNo}` : 'N/A'),
                        academicYear: storedData.student?.academicYear || (user?.procYear ? `${user.procYear}-${(user.procYear || 2024) + 1}` : 'N/A'),
                        email: storedData.student?.email || user?.email,
                        phone: storedData.student?.phone || user?.phone
                    });
                    setTransactionDate(new Date().toLocaleString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }));
                    setLoading(false);
                    return;
                }

                // ============================================================
                // STEP 6: If no data found
                // ============================================================
                setError('No payment data found');
                setStatus('failed');
                setLoading(false);

            } catch (error) {
                console.error('Error processing payment:', error);
                setError(error.message || 'Failed to process payment');
                setStatus('failed');
                setLoading(false);
            }
        };

        verifyPayment();
    }, [location, user]);

    const formatCurrency = (amount) => {
        if (!amount) return '₹0';
        const num = parseFloat(amount);
        return `₹${num.toLocaleString('en-IN')}`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return transactionDate || 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return dateString;
            }
            return date.toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const getStatusConfig = () => {
        const configs = {
            success: {
                icon: <CheckCircle className="w-12 h-12" />,
                title: 'Payment Successful',
                message: 'Your exam fee has been paid successfully through CCAvenue.',
                headerBg: 'bg-gradient-to-r from-emerald-500 to-green-600',
                bodyBg: 'bg-gradient-to-b from-emerald-50 via-green-50 to-emerald-25',
                iconColor: 'text-emerald-600',
                textColor: 'text-emerald-700',
                borderColor: 'border-emerald-200',
                buttonColor: 'bg-emerald-600 hover:bg-emerald-700',
                lightColor: 'bg-emerald-50',
                mediumColor: 'bg-emerald-100',
                darkColor: 'bg-emerald-600',
                greetingIcon: <PartyPopper className="w-8 h-8" />
            },
            failed: {
                icon: <XCircle className="w-12 h-12" />,
                title: 'Payment Failed',
                message: 'Transaction could not be completed through CCAvenue.',
                headerBg: 'bg-gradient-to-r from-rose-500 to-red-600',
                bodyBg: 'bg-gradient-to-b from-rose-50 via-red-50 to-rose-25',
                iconColor: 'text-rose-600',
                textColor: 'text-rose-700',
                borderColor: 'border-rose-200',
                buttonColor: 'bg-rose-600 hover:bg-rose-700',
                lightColor: 'bg-rose-50',
                mediumColor: 'bg-rose-100',
                darkColor: 'bg-rose-600'
            },
            cancelled: {
                icon: <AlertCircle className="w-12 h-12" />,
                title: 'Payment Cancelled',
                message: 'You have cancelled the payment process.',
                headerBg: 'bg-gradient-to-r from-amber-500 to-orange-600',
                bodyBg: 'bg-gradient-to-b from-amber-50 via-orange-50 to-amber-25',
                iconColor: 'text-amber-600',
                textColor: 'text-amber-700',
                borderColor: 'border-amber-200',
                buttonColor: 'bg-amber-600 hover:bg-amber-700',
                lightColor: 'bg-amber-50',
                mediumColor: 'bg-amber-100',
                darkColor: 'bg-amber-600'
            },
            pending: {
                icon: <Clock className="w-12 h-12" />,
                title: 'Payment Pending',
                message: 'Your payment is being processed by CCAvenue.',
                headerBg: 'bg-gradient-to-r from-amber-500 to-orange-600',
                bodyBg: 'bg-gradient-to-b from-amber-50 via-orange-50 to-amber-25',
                iconColor: 'text-amber-600',
                textColor: 'text-amber-700',
                borderColor: 'border-amber-200',
                buttonColor: 'bg-amber-600 hover:bg-amber-700',
                lightColor: 'bg-amber-50',
                mediumColor: 'bg-amber-100',
                darkColor: 'bg-amber-600'
            },
            default: {
                icon: <AlertCircle className="w-12 h-12" />,
                title: 'Payment Status',
                message: 'Processing payment status...',
                headerBg: 'bg-gradient-to-r from-gray-500 to-gray-600',
                bodyBg: 'bg-gradient-to-b from-gray-50 via-gray-50 to-gray-25',
                iconColor: 'text-gray-600',
                textColor: 'text-gray-700',
                borderColor: 'border-gray-200',
                buttonColor: 'bg-gray-600 hover:bg-gray-700',
                lightColor: 'bg-gray-50',
                mediumColor: 'bg-gray-100',
                darkColor: 'bg-gray-600'
            }
        };

        return configs[status] || configs.default;
    };

    const config = getStatusConfig();

    const handleDownloadReceipt = async () => {
        try {
            setIsDownloading(true);
            
            const response = await postData('/HallTicket/DownloadReceipt', {
                transactionId: paymentData?.txnId,
                rollNo: studentDetails?.rollNo,
                amount: paymentData?.amount,
                gateway: 'ccavenue'
            });

            setIsDownloading(false);

            if (response?.success) {
                Swal.fire({
                    title: 'Success!',
                    text: 'Receipt downloaded successfully',
                    icon: 'success',
                    confirmButtonColor: config.darkColor,
                    timer: 2000
                });
            } else {
                throw new Error(response?.message || 'Failed to download receipt');
            }
        } catch (error) {
            console.error('Error downloading receipt:', error);
            setIsDownloading(false);
            Swal.fire({
                title: 'Error',
                text: error.message || 'Failed to download receipt. Please try again.',
                icon: 'error',
                confirmButtonColor: config.darkColor
            });
        }
    };

    const handleRetryPayment = () => {
        sessionStorage.removeItem('ccavenuePaymentData');
        sessionStorage.removeItem('ccavenueResponseData');
        sessionStorage.removeItem('paymentDetails');
        navigate('/exam-fees');
    };

    const handleBackToFees = () => {
        sessionStorage.removeItem('ccavenuePaymentData');
        sessionStorage.removeItem('ccavenueResponseData');
        sessionStorage.removeItem('paymentDetails');
        navigate('/exam-fees');
    };

    const handleContactSupport = () => {
        navigate('/support');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Verifying CCAvenue payment...</p>
                    <p className="text-sm text-gray-400 mt-2">Please wait while we confirm your transaction</p>
                </div>
            </div>
        );
    }

    if (error && !paymentData) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-12 h-12 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Payment Error</h3>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <div className="space-y-3">
                        <button
                            onClick={handleRetryPayment}
                            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-blue-600 hover:to-indigo-700 transition duration-200"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${config.bodyBg} py-8 px-4 transition-colors duration-300`}>
            <div className="max-w-5xl mx-auto border-2 border-gray-200 rounded-xl bg-white shadow-2xl overflow-hidden">
                {/* Header Status Card */}
                <div className={`shadow-xl ${config.headerBg} text-white p-8 transform transition-all duration-300 hover:shadow-2xl`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between">
                        <div className="flex items-center space-x-6 mb-6 md:mb-0">
                            <div className="bg-white/20 p-4 rounded-full backdrop-blur-sm">
                                {React.cloneElement(config.icon, { className: "w-16 h-16 text-white" })}
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold mb-2">{config.title}</h1>
                                <p className="text-white/90 text-lg">{config.message}</p>
                                {status === 'success' && (
                                    <div className="flex items-center mt-3">
                                        <Mail className="w-5 h-5 mr-2" />
                                        <span className="text-sm">Receipt sent to your registered email</span>
                                    </div>
                                )}
                                {status === 'cancelled' && (
                                    <div className="flex items-center mt-3">
                                        <AlertCircle className="w-5 h-5 mr-2" />
                                        <span className="text-sm">No charges have been applied</span>
                                    </div>
                                )}
                                <div className="flex items-center mt-2 text-sm bg-white/10 px-3 py-1 rounded-full">
                                    <span className="mr-2">Gateway:</span>
                                    <span className="font-semibold">CCAvenue</span>
                                    <span className="mx-2">•</span>
                                    <span className="font-semibold uppercase">{status}</span>
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-4xl md:text-5xl font-bold mb-2">{formatCurrency(paymentData?.amount)}</div>
                            <p className="text-white/80">
                                {formatDate(paymentData?.txndate || paymentData?.timestamp || Date.now())}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Two Column Details */}
                <div className="grid lg:grid-cols-2 gap-1 mb-1">
                    {/* Left Column: Student Details */}
                    <div className={`bg-white shadow-lg border ${config.borderColor} p-6 transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-center justify-between mb-8">
                            <h3 className={`text-base font-bold ${config.textColor} flex items-center`}>
                                <GraduationCap className={`w-6 h-6 mr-3 ${config.iconColor}`} />
                                Student Details
                            </h3>
                            <div className={`px-3 py-1 ${config.lightColor} rounded-full`}>
                                <span className={`text-sm font-medium ${config.textColor}`}>
                                    {status === 'success' ? '✓ Verified' : status.toUpperCase()}
                                </span>
                            </div>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <User className="w-4 h-4 mr-2 text-gray-400" />
                                        Student Name
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-semibold text-gray-800 text-xl">
                                        {studentDetails?.name || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Hash className="w-4 h-4 mr-2 text-gray-400" />
                                        Roll Number
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-mono font-semibold text-gray-800 text-lg">
                                        {studentDetails?.rollNo || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Building className="w-4 h-4 mr-2 text-gray-400" />
                                        Department
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {studentDetails?.department || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <BookOpen className="w-4 h-4 mr-2 text-gray-400" />
                                        Semester
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {studentDetails?.semester || 'N/A'}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                        Academic Year
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {studentDetails?.academicYear || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Transaction Details */}
                    <div className={`bg-white shadow-lg border ${config.borderColor} p-6 transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-center justify-between mb-8">
                            <h3 className={`text-base font-bold ${config.textColor} flex items-center`}>
                                <CreditCard className={`w-6 h-6 mr-3 ${config.iconColor}`} />
                                Transaction Details
                            </h3>
                            <div className={`px-3 py-1 ${config.lightColor} rounded-full`}>
                                <span className={`text-sm font-medium ${config.textColor}`}>
                                    CCAvenue
                                </span>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {paymentData?.receiptNo && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <Receipt className="w-4 h-4 mr-2 text-gray-400" />
                                            Receipt Number
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className={`font-mono font-semibold text-lg ${config.textColor} ${config.lightColor} px-4 py-2 rounded-lg block`}>
                                            {paymentData?.receiptNo}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {paymentData?.txnId && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <FileText className="w-4 h-4 mr-2 text-gray-400" />
                                            Transaction ID
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className="font-mono font-semibold text-gray-800 text-lg">
                                            {paymentData?.txnId}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {paymentData?.orderId && (
                                <div>
                                    <div className="flex items-center mb-2">
                                        <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                        <span className="text-gray-600 font-medium flex items-center">
                                            <Hash className="w-4 h-4 mr-2 text-gray-400" />
                                            Order ID
                                        </span>
                                    </div>
                                    <div className="pl-5">
                                        <span className="font-mono font-semibold text-gray-800 text-lg">
                                            {paymentData?.orderId}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="flex items-center mb-2">
                                    <div className={`w-2 h-2 rounded-full ${config.mediumColor} mr-3`}></div>
                                    <span className="text-gray-600 font-medium flex items-center">
                                        <Clock className="w-4 h-4 mr-2 text-gray-400" />
                                        Transaction Date & Time
                                    </span>
                                </div>
                                <div className="pl-5">
                                    <span className="font-medium text-gray-800 text-lg">
                                        {formatDate(paymentData?.txndate || paymentData?.timestamp)}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                                <div>
                                    <div className="flex items-center mb-1">
                                        <Mail className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-xs text-gray-500">Email</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-800 break-all">
                                        {studentDetails?.email || paymentData?.email || 'N/A'}
                                    </span>
                                </div>
                                <div>
                                    <div className="flex items-center mb-1">
                                        <Phone className="w-4 h-4 text-gray-400 mr-2" />
                                        <span className="text-xs text-gray-500">Phone</span>
                                    </div>
                                    <span className="text-sm font-medium text-gray-800">
                                        {studentDetails?.phone || paymentData?.phone || 'N/A'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Section */}
                {status === 'success' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor} transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex flex-col items-center text-center">
                            <div className={`mb-6 p-4 rounded-full ${config.mediumColor}`}>
                                {React.cloneElement(config.greetingIcon, { className: `w-12 h-12 ${config.iconColor}` })}
                            </div>
                            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${config.textColor}`}>
                                CCAvenue Payment Completed Successfully! 🎉
                            </h2>
                            <div className={`max-w-2xl mx-auto mb-8 p-4 ${config.mediumColor} rounded-lg`}>
                                <p className="text-lg font-medium text-gray-700">
                                    Your exam fee has been paid successfully through CCAvenue.
                                </p>
                                <p className={`text-xl font-bold mt-2 ${config.textColor}`}>
                                    Hall Ticket will be available soon.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                                <button
                                    onClick={handleDownloadReceipt}
                                    disabled={isDownloading}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl 
                                    bg-gradient-to-r from-emerald-500 to-teal-600 
                                    text-white font-semibold shadow-lg 
                                    hover:from-emerald-600 hover:to-teal-700 
                                    hover:shadow-xl active:scale-95 
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                    transition-all duration-200"
                                >
                                    {isDownloading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-5 h-5" />
                                            Download Receipt
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleBackToFees}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl
                                    bg-white border-2 border-emerald-200 text-emerald-700 font-semibold
                                    hover:bg-emerald-50 hover:border-emerald-300
                                    transition-all duration-200"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                    Back to Fees
                                </button>
                            </div>
                        </div>
                    </div>
                ) : status === 'failed' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor} transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-start">
                            <div className={`p-3 rounded-full ${config.mediumColor} mr-4 flex-shrink-0`}>
                                <Info className={`w-6 h-6 ${config.iconColor}`} />
                            </div>
                            <div className="flex-1">
                                <h4 className={`text-xl font-bold mb-4 ${config.textColor}`}>
                                    📋 Important Information
                                </h4>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">You can retry the payment from your dashboard</p>
                                        </div>
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">No charges will be applied for failed transactions</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">Contact support if amount was deducted but failed</p>
                                        </div>
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">Check your bank statement for any deductions</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 justify-center mt-8">
                            <button
                                onClick={handleBackToFees}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl
                                bg-white border-2 border-gray-300 text-gray-700 font-semibold
                                hover:bg-gray-50 hover:border-gray-400
                                transition-all duration-200"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                Back to Fees
                            </button>
                            <button
                                onClick={handleRetryPayment}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl
                                bg-gradient-to-r from-rose-500 to-red-600
                                text-white font-semibold shadow-lg
                                hover:from-rose-600 hover:to-red-700
                                transition-all duration-200"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Retry Payment
                            </button>
                        </div>
                    </div>
                ) : status === 'cancelled' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor} transform transition-all duration-300 hover:shadow-xl`}>
                        <div className="flex items-start">
                            <div className={`p-3 rounded-full ${config.mediumColor} mr-4 flex-shrink-0`}>
                                <AlertCircle className={`w-6 h-6 ${config.iconColor}`} />
                            </div>
                            <div className="flex-1">
                                <h4 className={`text-xl font-bold mb-4 ${config.textColor}`}>
                                    📋 Payment Cancelled
                                </h4>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">No charges have been applied to your account</p>
                                        </div>
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">You can retry the payment anytime</p>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">Contact support if you face any issues</p>
                                        </div>
                                        <div className="flex items-start">
                                            <div className={`w-2 h-2 rounded-full mt-2 mr-3 ${config.darkColor}`}></div>
                                            <p className="text-gray-700">Check your dashboard for payment status</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 justify-center mt-8">
                            <button
                                onClick={handleBackToFees}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl
                                bg-white border-2 border-gray-300 text-gray-700 font-semibold
                                hover:bg-gray-50 hover:border-gray-400
                                transition-all duration-200"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                Back to Fees
                            </button>
                            <button
                                onClick={handleRetryPayment}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl
                                bg-gradient-to-r from-amber-500 to-orange-600
                                text-white font-semibold shadow-lg
                                hover:from-amber-600 hover:to-orange-700
                                transition-all duration-200"
                            >
                                <RefreshCw className="w-5 h-5" />
                                Retry Payment
                            </button>
                        </div>
                    </div>
                ) : status === 'pending' || status === 'processing' ? (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor}`}>
                        <div className="flex flex-col items-center text-center">
                            <div className={`p-4 rounded-full ${config.mediumColor} mb-4`}>
                                <Clock className={`w-12 h-12 ${config.iconColor}`} />
                            </div>
                            <h2 className={`text-2xl font-bold mb-2 ${config.textColor}`}>
                                Payment Pending
                            </h2>
                            <p className="text-gray-600 mb-6">Your CCAvenue payment is being processed. Please wait.</p>
                            <button
                                onClick={handleBackToFees}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl
                                bg-gradient-to-r from-amber-500 to-orange-600
                                text-white font-semibold shadow-lg
                                hover:from-amber-600 hover:to-orange-700
                                transition-all duration-200"
                            >
                                <ArrowLeft className="w-5 h-5" />
                                Back to Dashboard
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className={`shadow-lg border ${config.borderColor} p-8 ${config.lightColor}`}>
                        <div className="flex flex-col items-center text-center">
                            <div className={`p-4 rounded-full ${config.mediumColor} mb-4`}>
                                <AlertCircle className={`w-12 h-12 ${config.iconColor}`} />
                            </div>
                            <h2 className={`text-2xl font-bold mb-2 ${config.textColor}`}>
                                Status Unknown
                            </h2>
                            <p className="text-gray-600 mb-6">Unable to determine payment status. Please contact support.</p>
                            <button
                                onClick={handleContactSupport}
                                className="flex items-center gap-2 px-6 py-3 rounded-xl
                                bg-gradient-to-r from-blue-500 to-indigo-600
                                text-white font-semibold shadow-lg
                                hover:from-blue-600 hover:to-indigo-700
                                transition-all duration-200"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Contact Support
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CCAvenuePaymentStatus;